import { SortModelItem } from 'ag-grid-enterprise'
import { claimEntitiesDetails } from 'features/claims/claimsModel'
import { ClaimEnum, ClaimErrorProblemTypeEnum, ClaimsEOBStatusEnum, ClaimsTypeEnum } from 'features/claims/types'
import { IClaimActivities, IEntityBag } from 'features/entities/types'
import { IColumn, IRow, OBGridFilter } from 'types/tableTypes'

// HTTP Types
export interface ICSVImportPostResponse {
  id: string
  fileName: string
  rejectedLineCount: number
  hadHeader: boolean
  fatalHeaderProblem: boolean
  claimCount: number
  updated: string
  created: string
  csvClaims: IClaim[]
  csvImportProblems: IClaimError[]
  detail?: string
  missingColumns?: []
}
export interface IGetClaimsResponse {
  count: number
  limit: number
  offset: number
  data: IClaim[]
}

export interface IGetUniqueValuesResponse {
  count: number
  limit: number
  offset: number
  data: [
    {
      group: string
      count: number
    },
  ]
}

export interface IGetClaimsHistoryVersion {
  count: number
  limit: number
  offset: number
  data: [
    {
      entityId: string
      entityValue: string
      actorName: string
      actorId: string
      callJobId: string
      remarks: string
      dateCreated: string
      current: boolean
    },
  ]
}
export interface IClaimError {
  id: string
  problemType: ClaimErrorProblemTypeEnum
  line: number
  column?: string
  instance: string
  detail: string
}

type ClaimFieldsType = {
  [key in ClaimEnum]: string
}

export interface IClaim extends Omit<ClaimFieldsType, ClaimEnum.claimAmount | ClaimEnum.outstandingBalance> {
  [ClaimEnum.claimAmount]: number
  [ClaimEnum.outstandingBalance]: number
  archived: boolean
  entityBag: IEntityBag
  eobStatus: ClaimsEOBStatusEnum
  insurerPhoneNumber?: string
  claimStatusPayer?: string
  detailedClaimStatus?: string
  claimStatusCode?: number | null
  detailedClaimStatusCode?: number | null
}

export interface IParsedClaim {
  id: string
  [k: string]: string | number | boolean
}

export interface IClaimHistoryResponse {
  count: number
  limit: number
  offset: number
  data: IClaimActivities[]
}

// Claims Misc Types
export type claimDetailsType = keyof typeof claimEntitiesDetails
export interface IClaimEntities {
  [k: string]: {
    name: string
    value: string | null
    type: string
  }
}
export interface ITable {
  params?: any
  rows: null | IRow[]
  itemsPerPage: number
  totalItems: number
  page: number
  apiQuery: string
  sorting: SortModelItem[]
  filters: OBGridFilter
  statusFilter: ClaimsTypeEnum
}

export interface IUpdateTableRequest {
  prop: 'rows' | 'itemsPerPage' | 'totalItems' | 'page' | 'sorting' | 'apiQuery'
  value: string | number | null
}

export interface IUpdateTableSortRequest {
  prop: 'rows' | 'itemsPerPage' | 'totalItems' | 'page' | 'sorting' | 'apiQuery'
  value: SortModelItem[] | string | number | null
}

export interface IPreProcessing {
  table: ITable
}

export interface IClaimsTableRoot {
  params?: any
  rows: null | IRow[]
  itemsPerPage: number
  totalItems: number
  page: number
  apiQuery: string
  sorting: SortModelItem[]
  filters: OBGridFilter
  statusFilter: ClaimsTypeEnum
  count: number | undefined
  selectedRow?: any[]
}

export interface IFileUploadError {
  processedClaimCount?: number
  failedClaimCount?: number
  claimCount?: number
  missingColumns?: undefined | []
  data?: {
    columns: IColumn[]
    rows: IRow[]
  }
  detail?: string
}

export interface IEOBRequest {
  JobId: string
  EOBStatus: ClaimsEOBStatusEnum
  LastCallCompletionTime: string
}

export interface IClaimDispositionList {
  claimDispositionList: string[]
}

export const claimDispositionsList = [
  'Paid or Approved',
  'Denied',
  'Processing',
  'Claim on File',
  'Claim not on file',
  'Undefined',
]

export enum ClaimWorkflowStatus {
  unknown = 'Unknown',
  inProgress = 'In Progress',
  workCompleted = 'Work Completed',
  statusUpdateNeeded = 'Status Update Needed',
}

export interface IComments {
  noteId: string
  oaiClaimId: string
  note: string
  noteCreator: string
  created: string
  updated: string
  noteCreatorUser: IProfile
}

export interface IClaimComments {
  count: number
  data: IComments[]
  limit: number
  offset: number
}

export interface IProfile {
  firstName: string
  lastName: string
}

export type supportedOperations = 'FetchECS | Get a Rep  | NotSupported | FetchEBV'

export enum callSupportedOperations {
  FetchECS = 'FetchECS',
  GetARep = 'Get a Rep',
  NotSupported = 'NotSupported',
  FetchEBV = 'FetchEBV',
  FetchEOB = 'FetchEOB',
  // TODO: mock value until Fetch ASC is wired to a real use case
  FetchASC = 'FetchASC',
}

export interface ILevelOfSupport {
  levelOfSupport: string | null
  supportedOperations: supportedOperations[]
}

export interface ISupportedOperations {
  [key: string]: [
    {
      levelOfSupport: string | null
      supportedOperations: supportedOperations[]
    },
  ]
}

export interface IClaimDetailsExtracted {
  label: string
  value: string | undefined
  isEditedValue: boolean
  typeId: string | undefined
  hasMatch?: boolean
  version?: string[]
  toolTipText?: string | null
  toolTipfor?: string
  isCurrency?: boolean
  doValueMatch?: boolean
}

export interface IAllClaimRouteUrl {
  claimStatus?: ClaimStatusForRouteType
  navigationTab?: NavigationTabsBarType
  batchId?: string
  batchCode?: string
}

export type NavigationTabsBarType = 'needs-work' | 'on-live-call' | 'queued' | 'archived'
export type ClaimStatusForRouteType =
  | 'all'
  | 'paid'
  | 'denied'
  | 'processing'
  | 'claim-on-file'
  | 'claim-not-on-file'
  | 'undefined'
  | 'ivr-response'
  | 'coverage-issue'
